/**
 * A field displays label(s), input(s), and any error(s).
 */
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useContext
} from "react";
import cn from "clsx";
import { InputCustomChangeEventHandler } from "../../types";
import {
  isString,
  isEmpty,
  isArray,
  get,
  has,
  hasAndIsNotEmpty,
  findByPropFn
} from "../../utils/helpers";
import useT from "../../hooks/useT";
import { AppStateContext } from "../../containers/App";
import { ReactSelectChangeEventHandler } from "../InputSelectCustom";
import Input, { InputElementProps } from "../Input";
import Icon from "../Icon";
import Button from "../Button";
import Errors from "../Errors";
import Label from "./Label";
import "./Field.css";
import ValidLableMessage from "../ValidLableMessage";

export interface FieldProps {
  /** The field type. Primarily used if not specifying the field's input via children. */
  type?: string;
  /** The field's ID. Used for label targeting purposes. */
  id: string;
  /** The field's name. Used for `onChange` event handlers. */
  name: string;
  /** The field's value. Should always be passed so the field knows to add/remove contextual classes for styling. */
  value: any;
  /** Additional class names to apply. */
  className?: string;
  /** A text label describing the field. */
  label?: React.ReactNode | React.ReactNodeArray;
  /** An additional aria-label describing the field. */
  "aria-label"?: string;
  /** Mark if the field is required (will display a label). */
  required?: boolean;
  /** Mark if the field requires that user enter their data in a non-roman based language, e.g. English. */
  enterInRoman?: boolean;
  /** Mark if the field label should be displayed*/
  hideLabel?: boolean;
  /** An icon to display next to the field's input. */
  icon?: React.ReactNode;
  /** Mark the language code of the field with an IEFT language/locale value. Will affect translations. */
  lang?: string;
  /** Display a button to clear the input. If enabled can also accept an onClear event handler. */
  clearable?: boolean;
  /** Mark the input as disabled. */
  disabled?: boolean;
  /** You can render your own input components within the allocated space. These components event handlers will need to be managed separately. */
  children?: React.ReactNode | React.ReactNodeArray;
  /** A list of error messages to display on the field. */
  errors?: React.ReactNodeArray;
  /** Event handler for when the field's input element is focused. */
  onFocus?: React.FocusEventHandler;
  /** Event handler for when the field's input element is blurred. */
  onBlur?: React.FocusEventHandler;
  /** Event handler for when the field's input element is changed. */
  onChange?:
    | React.ChangeEventHandler
    | InputCustomChangeEventHandler
    | ReactSelectChangeEventHandler;
  /** Event handler for when the field's `clearable` button is clicked. */
  onClear?: React.MouseEventHandler;
  /** Extra props to pass to the generated input component (only if not using `children`). */
  inputProps?: object;
  placeholder?: string;
  validLabelMessagse?: React.ReactNodeArray;
}

// Toggleable components have a different layout
const COMPONENT_TYPE_IS_TOGGLEABLE = [
  "checkbox",
  "InputCheckbox",
  "radio",
  "InputRadio",
  "toggleable",
  "InputToggleable"
];

// Clearable components might need some extra events & styling considerations
const COMPONENT_TYPE_SUPPORTS_CLEARABLE = [
  "select",
  "InputSelect",
  "dropdown",
  "select-custom",
  "InputSelectCustom",
  "dropdownCustom"
];

// These components can have options, which means that a field can have a valid
// value of null, "" or false.
const COMPONENT_TYPE_HAS_OPTIONS = [
  "checkbox",
  "InputCheckbox",
  "radio",
  "radioGroup",
  "InputRadioGroup",
  "InputRadio",
  "toggleable",
  "InputToggleable",
  "select",
  "InputSelect",
  "dropdown",
  "select-custom",
  "InputSelectCustom",
  "dropdownCustom",
  "country",
  "addressCountry",
  "InputCountry",
  "region",
  "country-region",
  "countryRegion",
  "addressRegion",
  "InputCountryRegion",
  "title",
  "honorificPrefix",
  "InputTitle"
];

const INVALID_VALUES = [undefined, null, ""];
const INVALID_VALUES_FOR_OPTIONS = [undefined, null];
const INVALID_VALUES_FOR_TOGGLEABLE = [undefined, null, "", false];

/**
 * Take a number of event handler functions and wrap them into a single
 * function that will fire them all with the passed arguments when called.
 */
function wrapEventHandlerFn(...eventHandlers: Function[]): Function {
  const handlerFns = eventHandlers.filter(handler => handler);
  return (...args: any[]) => {
    handlerFns.forEach(handler => handler(...args));
  };
}

/**
 * Populate the inputProps object with a wrapped event handler function.
 *
 * @NOTE this mutates the given inputProps object.
 */
function populateInputPropsWithWrappedEventHandlers(
  inputProps: InputElementProps,
  eventHandlerName: string,
  ...eventHandlers: Function[]
): InputElementProps {
  // If the eventHandler already exists on the inputProps, add it to the list
  if (hasAndIsNotEmpty(inputProps, eventHandlerName)) {
    eventHandlers.push(inputProps[eventHandlerName]);
  }

  // Wrap the multiple event handlers
  if (eventHandlers.length > 1) {
    inputProps[eventHandlerName] = wrapEventHandlerFn(...eventHandlers);
  }
  // Only assign the one event handler
  else if (eventHandlers.length === 1) {
    inputProps[eventHandlerName] = eventHandlers[0];
  }

  return inputProps;
}

export default function Field({
  id,
  name,
  value,
  type,
  className,
  label,
  required = false,
  hideLabel = false,
  enterInRoman = false,
  lang,
  icon,
  clearable,
  errors,
  validLabelMessagse,
  disabled,
  placeholder,
  children,
  onFocus,
  onBlur,
  onChange,
  onClear,
  inputProps = {}
}: FieldProps) {
  // Shared and internal state
  // console.log("from where do you come?", errors);
  const { t, i18n } = useT();
  const [focused, setFocused] = useState(false);
  const [customError, setCustomError] = useState("");
  const [customLable, setCustomLable] = useState("");
  const ref = useRef({
    mounted: false
  });

  const lng = lang || i18n.language;
  const [appState] = useContext(AppStateContext);
  const config = appState.config;

  // Event handlers
  const handleFocus = useCallback<React.FocusEventHandler>(
    (...args) => {
      if (ref.current && ref.current.mounted) {
        setFocused(true);
      }
      if (onFocus) {
        onFocus(...args);
      }
    },
    [onFocus]
  );
  const handleBlur = useCallback<React.FocusEventHandler>(
    (...args) => {
      if (ref.current && ref.current.mounted) {
        setFocused(false);
      }
      if (onBlur) {
        onBlur(...args);
      }
    },
    [onBlur]
  );
  const handleChange = useCallback<React.ChangeEventHandler>(
    (...args: any[]) => {
      if (onChange) {
        onChange(args[0], args[1]);
      }
    },
    [onChange]
  );
  const handleClear = useCallback<React.MouseEventHandler>(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (ref.current && ref.current.mounted) {
        setFocused(false);
      }
      if (onClear) {
        onClear(event);
      }
    },
    [onClear]
  );

  // Memoized values
  // -- Check if the field has a value
  const hasValue = useMemo(() => {
    let _hasValue = !INVALID_VALUES.includes(value);

    // Check if value is in the options
    if (type && COMPONENT_TYPE_HAS_OPTIONS.includes(type)) {
      // If the input has options and the option's label doesn't have an empty
      // string, then we class the field as having a value.
      if (has(inputProps, "options", isArray)) {
        // Can have multiple values
        if (has(inputProps, "multiple")) {
          _hasValue = isArray(value) && value.length > 0;
        }
        // Search for single value
        else {
          const selectedOption = get(inputProps, "options").find(
            findByPropFn("value", value)
          );

          _hasValue = !!selectedOption && selectedOption.label !== "";
        }
      }
      // Input types like options but not having an `inputProps.options` prop.
      else {
        _hasValue = !INVALID_VALUES_FOR_OPTIONS.includes(value);
      }
    }

    // Check if toggleable value is toggled on
    if (type && COMPONENT_TYPE_IS_TOGGLEABLE.includes(type)) {
      _hasValue = !INVALID_VALUES_FOR_TOGGLEABLE.includes(value);
    }

    return _hasValue;
  }, [value, get(inputProps, "options")]);

  // -- Output any extra labels
  const extraLabels = useMemo<React.ReactNodeArray>(
    () =>
      [
        required && config.customise.fields.showRequiredLabel ? (
          config.customise.fields.markRequiredWithSymbol ? (
            <span aria-label={t("general.fields.required.label", { lng })}>
              {config.customise.fields.markRequiredWithSymbol}
            </span>
          ) : (
            t("general.fields.required.label", { lng })
          )
        ) : (
          ""
        ),
        !required && config.customise.fields.showOptionalLabel
          ? t("general.fields.optional.label", { lng })
          : "",
        enterInRoman &&
        config.languages.nonRomanLanguages.includes(lang || i18n.language) &&
        !get(config, ["zones", appState.zone, "languages"]).includes(
          lang || i18n.language
        )
          ? t("general.fields.enterInRoman.label", { lng })
          : ""
      ].filter(extraLabel => !!extraLabel),
    [t, lng, required, enterInRoman]
  );

  // Ensure the field's event handlers wrap the inputs
  const wrappedInputProps = useMemo(() => {
    const _wrappedInputProps: InputElementProps = {
      disabled,
      // Ensure that the field also passed through the language prop to the input
      lang: lng,
      ...inputProps
    };

    if (!!type && !children) {
      // Wrap onFocus
      populateInputPropsWithWrappedEventHandlers(
        _wrappedInputProps,
        "onFocus",
        handleFocus
      );

      // Wrap onBlur
      populateInputPropsWithWrappedEventHandlers(
        _wrappedInputProps,
        "onBlur",
        handleBlur
      );

      // Wrap onChange
      populateInputPropsWithWrappedEventHandlers(
        _wrappedInputProps,
        "onChange",
        handleChange
      );

      // Cater for components that already support `clearable`
      // and wrap onClear event handler
      if (COMPONENT_TYPE_SUPPORTS_CLEARABLE.includes(type)) {
        // Pass through the clearable value via inputProps
        _wrappedInputProps.clearable = clearable;

        populateInputPropsWithWrappedEventHandlers(
          _wrappedInputProps,
          "onClear",
          handleClear
        );
      }
    }
    // Pass the extraLabels to the input if the Field doesn't have a label to show
    if (hideLabel && extraLabels) {
      _wrappedInputProps.extraLabels = extraLabels;
    }

    if (placeholder) {
      _wrappedInputProps.placeholder = t(placeholder);
    }

    return _wrappedInputProps;
  }, [
    lng,
    label,
    extraLabels,
    inputProps,
    onFocus,
    onBlur,
    onChange,
    onClear,
    clearable,
    disabled,
    placeholder
  ]);

  useEffect(() => {
    ref.current.mounted = true;
    return () => {
      ref.current.mounted = false;
    };
  });

  const onCustomError = (error: string) => {
    setCustomError(error);
  };
  const onCustomLable = (label: string) => {
    setCustomLable(label);
  };
  const onClearCustomLable = () => {
    setCustomLable("");
  };
  const onClearCustomError = () => {
    setCustomError("");
  };

  if (customError) {
    errors = errors || [];

    while (errors.length) {
      errors.pop();
    }
    errors.push(customError);
  }
  if (customLable) {
    validLabelMessagse = validLabelMessagse || [];
    while (validLabelMessagse.length) {
      validLabelMessagse.pop();
    }
    validLabelMessagse.push(customLable);
  }

  return (
    <div
      id={`field-${id}`}
      className={cn(
        "field",
        `field-type-${type}`,
        !isEmpty(name) && `field-name-${name}`,
        !!type &&
          COMPONENT_TYPE_IS_TOGGLEABLE.includes(type) &&
          "is-toggleable",
        !isEmpty(icon) && "has-icon",
        !isEmpty(label) && "has-label",
        hasValue && "has-value",
        !isEmpty(errors) && "has-errors",
        disabled && "is-disabled",
        focused && "is-focused",
        className
      )}
    >
      <div className="field-control">
        {label && !isEmpty(label) && (
          <Label
            label={label}
            id={id}
            hideLabel={hideLabel}
            extraLabels={extraLabels}
          />
        )}
        <div className="field-input">
          {!isEmpty(icon) && isString(icon) && (
            <span className="field-icon">
              <Icon name={icon as string} />
            </span>
          )}
          {!!icon && !isString(icon) && (
            <span className="field-icon">{icon}</span>
          )}
          {!!type && !children && (
            <>
              <Input
                type={type}
                id={id}
                name={name}
                value={value}
                inputProps={wrappedInputProps}
                aria-required={required}
                aria-label={label}
                onCustomError={onCustomError}
                onClearCustomError={onClearCustomError}
                onSetLable={onCustomLable}
                onClaerCustomLabel={onClearCustomLable}
              />
              {!!clearable &&
                !COMPONENT_TYPE_SUPPORTS_CLEARABLE.includes(type) &&
                hasValue && (
                  <span className="clear-input">
                    <Button
                      icon="clear"
                      ariaLabel={t("general.buttons.clear.label", { lng })}
                      shape="none"
                      onClick={handleClear}
                    />
                  </span>
                )}
            </>
          )}
          {!type && children}
        </div>
      </div>
      <Errors classNamePrefix="field" errors={errors} />
      <ValidLableMessage classNamePrefix="field" text={validLabelMessagse} />
    </div>
  );
}
